import * as moment from "moment-timezone";
import { Action } from "./action";
import { AmendedAction } from "./amendedAction";
import { NotesAddedAction } from "./notesAddedAction";
import { OrderResponse } from "./orderResponse";
import { OrderTypeConfiguration } from "./orderTypeConfiguration";

export interface HistoryDetailSummary {
  actionTaken: string;
  changesMadeTo: string;
  previousValue: string;
  datetime: string;
  reason: string;
  timezone: string;
  displayName: string;
  showMoreHReason?: boolean;
  // CO-2133 / QA bug B6 (2026-05-12, revised 2026-05-13) — Optional fields
  // populated only for NCI-addendum history rows
  // (NON_COVERED_ITEM_ADDENDUM_GENERATED and _UPDATED). Originally added
  // to drive a click-through link to the patient's Consents page; the link
  // was removed in provider-panel after QA found it broke layout (Order
  // Entry runs outside the chart-context shell) and the raw addendum ID
  // never surfaced in the Consents table for visual correlation. Fields
  // remain on the interface because other history-rendering templates
  // (order-entry-om, list-orders) still reference them; remove once those
  // call sites are reconciled.
  nciAddendumId?: number;
  patientChartId?: number;
}

/**
 * CO-2133 / QA bug B6 (revised 2026-05-13) — Format an action's UTC
 * `Datetime` field into the Consents-table display shape so the Order
 * History Reason cell matches the addendum row in Consents byte-for-byte.
 * Mirrors `consents-details.ts#formatDateTime`: detects the viewer's local
 * timezone via `moment.tz.guess()` and produces e.g. "05/13/2026 18:12 (CST)".
 */
function formatAddendumDatetime(dateString: string | undefined | null): string {
  if (!dateString) return "-";
  const timezone = moment.tz.guess();
  return moment.utc(dateString).tz(timezone).format("MM/DD/YYYY HH:mm (z)");
}

/**
 * CO-2133 / QA bug B4 (2026-05-12) — Convert a wire-format addendum status
 * (uppercase enum name from patient-charts AddendumStatus.name(): PENDING /
 * DELIVERED / DECLINED / UNSIGNED / SIGNED / SENT) into a sentence-case label
 * for display in the Order History "Action Taken" cell. Returns null/empty
 * input unchanged so callers can fall back to the generic label.
 */
function formatAddendumStatusLabel(status: string | undefined | null): string {
  if (!status) return status as string;
  const lower = String(status).toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function getHistoryDetailSummary(
  order: Partial<OrderResponse>,
  orderTypeConfiguration: OrderTypeConfiguration
) {
  if (!order.history) {
    return [];
  }
  return order.history
    .filter(
      (action: Action) =>
        action.actionType && action.actionType != Action.ActionTypeEnum.Created
    )
    .map<HistoryDetailSummary[]>((action: Action) => {
      if (action.actionType == Action.ActionTypeEnum.Amended) {
        const amendedAction = action as AmendedAction;
        if (!amendedAction.properties) {
          return [];
        }
        return amendedAction.properties.map<HistoryDetailSummary>(
          (property) => {
            return {
              actionTaken: action.actionType,
              changesMadeTo: propertyNameToLabel(
                property.propertyName,
                orderTypeConfiguration
              ),
              previousValue: property.previousValueDisplay,
              datetime: action.datetime,
              reason: amendedAction.reason,
              timezone: action.timezone,
              displayName: action.displayName,
              showMoreHReason: false,
            };
          }
        );
      } else if (
        action.actionType ===
          Action.ActionTypeEnum.NonCoveredItemAddendumUpdated ||
        action.actionType ===
          Action.ActionTypeEnum.NonCoveredItemAddendumGenerated
      ) {
        // CO-2133 / QA bugs B4 + B6 (2026-05-12, revised 2026-05-13) — NCI
        // Addendum history rows.
        //
        // For NON_COVERED_ITEM_ADDENDUM_UPDATED rows, resolve the addendum's
        // new status into the Action Taken cell so History readers see WHICH
        // status was reached (Signed / Sent / Declined / Unsigned / Delivered
        // / Pending) instead of the generic "Updated". Falls back to the
        // generic label when `addendumStatus` is missing (defensive for any
        // pre-fix data on the order document).
        //
        // For NON_COVERED_ITEM_ADDENDUM_GENERATED rows, keep the generic
        // "Non-Covered Item Addendum Generated" label — "generated" is
        // already informative on its own.
        //
        // Reason cell: 2026-05-13 — the original click-through link to the
        // Consents page (using `nciAddendumId` + `patientChartId`) was
        // removed from provider-panel after QA found Order Entry runs
        // outside the chart-context shell so the navigation broke layout,
        // and the raw addendum ID never appears in the Consents table for
        // visual correlation anyway. Reason now renders the action's
        // Datetime formatted to match the Consents table column
        // (MM/DD/YYYY HH:mm (z)) so clinicians can correlate to the
        // addendum row in Consents by date match. The Action Taken column
        // already supplies the noun.
        //
        // `nciAddendumId` + `patientChartId` are still emitted because
        // other history-rendering templates (order-entry-om, list-orders)
        // still consume them; remove once those call sites are reconciled.
        //
        // The dynamic UPDATED string bypasses the `translate` pipe —
        // ngx-translate returns the input key unchanged when no translation
        // exists, so a human-readable phrase passes through verbatim. The
        // CANCELED branch in the templates is gated on `actionTaken ===
        // 'CANCELED'` so our composed string leaves that special-case alone.
        const isUpdated =
          action.actionType ===
          Action.ActionTypeEnum.NonCoveredItemAddendumUpdated;
        let actionTaken: string;
        if (isUpdated) {
          const statusLabel = formatAddendumStatusLabel(action.addendumStatus);
          actionTaken = statusLabel
            ? `Non-Covered Item Addendum status changed to ${statusLabel}`
            : "Non-Covered Item Addendum Updated";
        } else {
          actionTaken = "Non-Covered Item Addendum Generated";
        }
        return [
          {
            actionTaken,
            changesMadeTo: "-",
            previousValue: "-",
            datetime: action.datetime,
            reason: formatAddendumDatetime(action.datetime),
            timezone: action.timezone,
            displayName: action.displayName,
            showMoreHReason: false,
            // Kept for backward compat with order-entry-om / list-orders
            // templates that still render the click-through link.
            nciAddendumId: action.nciAddendumId,
            patientChartId: order.patientChartId,
          },
        ];
      } else if (Action.hasReason(action)) {
        let reason = action.reason;
        if (action.actionType === "DECLINED") {
          reason = action.declinedReason;
        }
        return [
          {
            actionTaken: action.actionType,
            changesMadeTo: "-",
            previousValue: "-",
            datetime: action.datetime,
            reason: reason,
            timezone: action.timezone,
            displayName: action.displayName,
            showMoreHReason: false,
          },
        ];
      } else if (action.notes !== undefined) {
        const notesAction = action as NotesAddedAction;
        return [
          {
            actionTaken: action.actionType,
            changesMadeTo: "-",
            previousValue: "-",
            datetime: action.datetime,
            reason: notesAction.notes,
            timezone: action.timezone,
            displayName: action.displayName,
            showMoreHReason: false,
          },
        ];
      } else {
        return [
          {
            actionTaken: action.actionType,
            changesMadeTo: "-",
            previousValue: "-",
            datetime: action.datetime,
            reason: "-",
            timezone: action.timezone,
            displayName: action.displayName,
            showMoreHReason: false,
          },
        ];
      }
    })
    .reduce((flattened, arr) => [...flattened, ...arr], []);
}
function propertyNameToLabel(
  propertyName: string,
  orderTypeConfiguration: OrderTypeConfiguration
): string {
  let label: string | undefined;
  const component = orderTypeConfiguration.components?.find((component) =>
    component.properties.some((property) => property.name === propertyName)
  );
  if (component) {
    const property = component.properties.find(
      (property) => property.name === propertyName
    );
    label = property.label;
  } else {
    switch (propertyName) {
      case "categoryOtherDescription":
        label = "Other Description";
        break;
      case "dosageStrength":
        label = "Strength";
        break;
      case "dosageForm":
        label = "Form";
        break;
      case "rxNormal":
        label = "Rx Normal";
        break;
      case "ndc":
        label = "NDC";
        break;
    }
  }
  if (label) {
    return label;
  }
  return "";
}



// WEBPACK FOOTER //
// ./src/models/order-management/historyDetailSummary.ts